import {
    Box,
    Caption,
    Export,
    IconButton,
    Loader,
    showToast,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import style from "./code-block.scss";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { Workflows } from "components/icons/Workflows";

interface Props {
    content: string;
    language: string;
    isLoading?: boolean;
    isWorkflow?: boolean;
}

const WORKFLOW_FILE_EXTENSION = "b5pson";
const V3_EDITOR_GENERATION = "V3";

const parseWorkflowJson = (content: string): Record<string, unknown> | undefined => {
    try {
        return JSON.parse(content) as Record<string, unknown>;
    } catch {
        return undefined;
    }
};

const getEditorGeneration = (workflowJson: Record<string, unknown> | undefined): unknown => {
    const editorMetadata = workflowJson?.editorMetadata;
    if (typeof editorMetadata !== "object" || editorMetadata === null || !("editorGeneration" in editorMetadata)) {
        return undefined;
    }
    return editorMetadata.editorGeneration;
};

export const CodeBlock = (props: Props) => {
    const { t } = useTranslation();
    const { aiToolsApi, openWorkflowEditor } = useAIPanelContext();

    const workflowJson = props.isWorkflow ? parseWorkflowJson(props.content) : undefined;
    const isV3Workflow = getEditorGeneration(workflowJson) === V3_EDITOR_GENERATION;

    const handleFileDownload = (content: string) => {
        const file = `blancco_agent_${props.isWorkflow ? `workflow` : props.language}_file.${props.isWorkflow ? WORKFLOW_FILE_EXTENSION : props.language}`;
        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file;
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };
    const handleOpenWorkflowEditor = () => {
        if (!workflowJson) {
            showToast(t("AiAgent.codeBlock.invalidWorkflowJson"), "error");
            return;
        }
        openWorkflowEditor([{ workflowJson }]);
    };
    return (
        <Box columnLayout className={classNames(style.codeBlockWrapper, style.syntaxHighlighterPreStyling)}>
            <Box gap={"Sm"} justifyContent={"SpaceBetween"}>
                <Box gap={"Sm"}>
                    {props.isLoading && <Loader white />}
                    <Caption className={style.languageTextColor}>{props.language?.toUpperCase()}</Caption>
                </Box>
                {!props.isLoading && (
                    <Box gap={"Xs"}>
                        {isV3Workflow && aiToolsApi === undefined && (
                            <Tooltip placement={"bottom"}>
                                <TooltipTrigger>
                                    <IconButton onClick={handleOpenWorkflowEditor} noHoverStyle>
                                        <Workflows color={"var(--app-aiPanel-codeBlock-actionButton)"} />
                                    </IconButton>
                                </TooltipTrigger>
                                <TooltipContent>{t("AiAgent.codeBlock.openInWorkflowEditor")}</TooltipContent>
                            </Tooltip>
                        )}
                        <IconButton onClick={() => handleFileDownload(props.content)} noHoverStyle>
                            <Export color={"--app-aiPanel-codeBlock-actionButton"} />
                        </IconButton>
                        <CopyToClipboard value={props.content} iconColor={"--app-aiPanel-codeBlock-actionButton"} />
                    </Box>
                )}
            </Box>
            <SyntaxHighlighter style={a11yDark} language={props.language}>
                {props.content}
            </SyntaxHighlighter>
        </Box>
    );
};
